import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { ApiResponse } from '@demoops/shared-types';
/** Which day a week starts on, for date pickers. Defined here since the
 *  schedule feature that used to own it was retired on 2026-08-10. */
export type WeekStartDay = 'sunday' | 'monday';

/**
 * SET-1 — the caller's own preferences (SETTINGS_REWORK_SPEC §1). The prefs
 * document holds ONLY what the user explicitly set; every absent key means
 * "today's default" (dashboard landing, Monday weeks, all notifications on).
 */
export type LandingPage = 'dashboard' | 'today' | 'projects';

export interface MyPrefs {
  landing_page?: LandingPage;
  week_start?: WeekStartDay;
  notifications?: Record<string, boolean>;
  /** Pinned sidebar shortcuts (2026-09-25): in-app routes, in the person's order. */
  nav_pins?: string[];
}

/** One entry per module from the backend notification-module registry. */
export interface NotificationModule {
  key: string;
  label: string;
}

export interface PreferencesPayload {
  prefs: MyPrefs;
  notification_modules: NotificationModule[];
}

export const preferencesKeys = {
  all: ['me', 'preferences'] as const,
};

export function useMyPreferences() {
  return useQuery({
    queryKey: preferencesKeys.all,
    queryFn: async () =>
      (await api.get<ApiResponse<PreferencesPayload>>('/api/v1/me/preferences')).data,
    retry: false,
  });
}

/**
 * PUT is a full replace of the prefs document — callers merge before saving.
 * The response echoes the stored document, so it seeds the cache directly.
 */
export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (prefs: MyPrefs) =>
      (await api.put<ApiResponse<PreferencesPayload>>('/api/v1/me/preferences', prefs)).data,
    onSuccess: (data) => qc.setQueryData(preferencesKeys.all, data),
  });
}

/** The caller's week-start preference; unset (or still loading) = Monday, today's behavior. */
export function useWeekStartPref(): WeekStartDay {
  const { data } = useMyPreferences();
  return data?.prefs.week_start ?? 'monday';
}
